import { useCallback, useEffect, useState } from "react";
import { useResumableSession, ResumePrompt } from "@/components/ResumeSession";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { QuestionRunner } from "@/components/QuestionRunner";
import { Mascot } from "@/components/Mascot";
import { JP } from "@/components/JP";
import { SubNav, ASSESSMENT_SUBNAV } from "@/components/SubNav";
import { usePrefs } from "@/context/PrefsContext";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

export function AssessmentIntro() {
  const { t } = usePrefs();
  const [hasPrev, setHasPrev] = useState(false);
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get("/assessment/latest").then(() => setHasPrev(true)).catch(() => {});
    api.get("/assessment/questions").then((r) => setInfo(r.data)).catch(() => {});
  }, []);
  return (
    <div className="mx-auto max-w-2xl fade-up" data-testid="assessment-intro-page">
      <p className="font-jpserif text-sm tracking-[0.3em] text-muted-foreground">診断テスト</p>
      <h1 className="mt-3 font-jpserif text-4xl sm:text-5xl">{t("aIntroTitle")}</h1>
      <div className="mt-5"><SubNav items={ASSESSMENT_SUBNAV} testid="assessment-subnav" /></div>
      <p className="mt-5 text-base leading-relaxed text-muted-foreground">{t("aIntroDesc")}</p>
      {info?.mascot && <div className="soft-card mt-6 p-5"><Mascot m={info.mascot} size={96} testid="assessment-intro-mascot" /></div>}
      {info?.batches?.length > 0 && (
        <div className="mt-6 grid gap-3 sm:grid-cols-3" data-testid="assessment-batches">
          {info.batches.map((b, i) => (
            <div key={b.level} className="soft-card p-4" data-testid={`assessment-batch-${b.level}`}>
              <div className="flex items-center justify-between"><span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("batch")} {i + 1}</span><span className="font-jpserif text-2xl">{b.level}</span></div>
              <JP text={b.title_jp} as="p" className="mt-2 text-sm font-medium" mode="off" />
              <p className="text-xs text-muted-foreground">{b.code} · {b.count} {t("question").toLowerCase()}</p>
            </div>
          ))}
        </div>
      )}
      <ul className="mt-8 space-y-3">
        {[t("aIntroP1"), t("aIntroP2"), t("aIntroP3"), t("aIntroP4")].map((s) => (
          <li key={s} className="flex items-start gap-3 text-sm"><span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sage-soft text-sage"><Check className="h-3 w-3" /></span>{s}</li>
        ))}
      </ul>
      <div className="mt-10 flex flex-wrap gap-3">
        <Link data-testid="assessment-begin-button" to="/assessment/run" className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5">{t("begin")}<ArrowRight className="h-4 w-4" /></Link>
        {hasPrev && <Link data-testid="assessment-view-result-link" to="/assessment/result" className="inline-flex items-center rounded-full border border-border px-7 py-3.5 text-sm font-semibold transition-colors hover:bg-accent">{t("resultTitle")}</Link>}
        {hasPrev && <Link data-testid="assessment-history-link" to="/assessment/history" className="inline-flex items-center rounded-full border border-border px-7 py-3.5 text-sm font-semibold transition-colors hover:bg-accent">{t("historyTitle")}</Link>}
      </div>
    </div>
  );
}

export function AssessmentRun() {
  const { t } = usePrefs();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [thinking, setThinking] = useState(null);
  const loader = useCallback(() => api.get("/assessment/questions").then((r) => r.data), []);
  const ses = useResumableSession("yk_assessment", loader);
  useEffect(() => { api.get("/mascots").then((r) => setThinking(r.data.find((m) => m.code === "berpikir"))).catch(() => {}); }, []);
  if (ses.saved) return <ResumePrompt saved={ses.saved} answered={ses.answered} onResume={ses.resume} onStartOver={ses.startOver} testid="assessment-resume-prompt" />;
  const data = ses.data;
  if (!data) return <div className="mx-auto max-w-2xl" data-testid="assessment-loading"><Mascot m={thinking} size={96} testid="loading-mascot" /><p className="mt-3 text-sm text-muted-foreground">{t("loading")}</p></div>;
  const submit = async (answers) => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/assessment/submit", { answers });
      ses.clear();
      nav("/assessment/result", { state: { result: res }, replace: true });
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const checkpoint = async (answers) => {
    try { return (await api.post("/review/checkpoint", { answers })).data; } catch (e) { toast.error(errMsg(e)); return null; }
  };
  return <div data-testid="assessment-run-page"><QuestionRunner key={data.questions[0]?.id} questions={data.questions} onSubmit={submit} submitting={busy} batchSize={data.batch_size} onCheckpoint={checkpoint} initialAnswers={ses.initialAnswers} initialIdx={ses.initialIdx} onProgress={ses.onProgress} /></div>;
}
